import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useToastContext from "hooks/useToastContext";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import BackgroundCheckList from "./components/BackgroundCheckList";
import BackgroundCheckDetail from "./components/BackgroundCheckDetail";
import BackgroundCheckForm from "./components/BackgroundCheckForm";

const BackgroundVerification = () => {
  const notification = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState('list');
  const [selectedCheck, setSelectedCheck] = useState(null);

  const [existingChecks, setExistingChecks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [checkTypes, setCheckTypes] = useState([]);

  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [hasLoadedEmployees, setHasLoadedEmployees] = useState(false);
  const [hasLoadedCheckTypes, setHasLoadedCheckTypes] = useState(false);

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    actionRequired: 0
  });

  const isMounted = useRef(false);

  const fetchEmployees = useCallback(async () => {
    if (hasLoadedEmployees) return;
    try {
      const result = await apiGet(apiPath.backgroundCandidatesList);
      if (result?.data?.success) {
        setEmployees(Array.isArray(result.data.results) ? result.data.results : []);
      }
    } catch (error) {
      console.error("Failed to load candidates:", error);
    } finally {
      setHasLoadedEmployees(true);
    }
  }, [hasLoadedEmployees]);

  const fetchCheckTypes = useCallback(async () => {
    if (hasLoadedCheckTypes) return;
    try {
      const result = await apiGet(apiPath.backgroundCheckTypes);
      if (result?.data?.success) {
        setCheckTypes(result.data.results?.check_types || []);
      }
    } catch (error) {
      console.error("Failed to load check types:", error);
    } finally {
      setHasLoadedCheckTypes(true);
    }
  }, [hasLoadedCheckTypes]);

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    const loadData = async () => {
      setIsLoadingLists(true);
      await Promise.all([fetchEmployees(), fetchCheckTypes()]);
      setIsLoadingLists(false);
    };
    loadData();
  }, [fetchEmployees, fetchCheckTypes]);

  // 🆕 Detect Stripe return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const txnId = searchParams.get('txn');
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'cancelled') {
      notification.warning("Payment cancelled. No background check was initiated.");
      setViewMode('list');
      setSearchParams({});
      return;
    }

    if (sessionId && txnId) {
      setViewMode('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleViewCheck = (check) => {
    setSelectedCheck(check);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCheck(null);
  };

  const handleCheckCreated = (newCheck) => {
    setExistingChecks(prev => [newCheck, ...prev]);
    setStats(prev => ({ ...prev, total: prev.total + 1, inProgress: prev.inProgress + 1 }));
    setViewMode('list');
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-LightBlue text-white' : 'bg-gray-200 text-gray-700'}`}
          >All Checks</button>
          <button
            onClick={() => setViewMode('new')}
            className={`px-4 py-2 rounded ${viewMode === 'new' ? 'bg-LightBlue text-white' : 'bg-gray-200 text-gray-700'}`}
          >New Check</button>
        </div>
      </div>

      {viewMode === 'new' ? (
        <BackgroundCheckForm
          onBack={handleBackToList}
          onCheckCreated={handleCheckCreated}
          stats={stats}
          employees={employees}
          checkTypes={checkTypes}
          isLoadingLists={isLoadingLists}
        />
      ) : viewMode === 'detail' ? (
        <BackgroundCheckDetail check={selectedCheck} onBack={handleBackToList} />
      ) : (
        <BackgroundCheckList
          onViewCheck={handleViewCheck}
          existingChecks={existingChecks}
          setExistingChecks={setExistingChecks}
          stats={stats}
          setStats={setStats}
        />
      )}
    </div>
  );
};

export default BackgroundVerification;